/* Milliard theme — collection/search filtering & sorting.
   One form (snippets/facets.liquid); changes re-render the main section via
   the Section Rendering API and update the URL. */
(() => {
  'use strict';

  /* Module-scoped: element instances are replaced on every re-render, so an
     instance property couldn't cancel a stale in-flight request. */
  let activeController = null;
  /* Set when the user closes the mobile drawer while a filter fetch is in
     flight, so the response handler doesn't yank it back open. */
  let drawerDismissed = false;

  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.sectionEl = this.closest('.shopify-section');
      this.sectionId = this.sectionEl ? this.sectionEl.id.replace('shopify-section-', '') : 'main-collection';

      this.debouncedSubmit = window.MBTheme.debounce(() => this.applyFilters(), 350);
      this.form.addEventListener('change', (e) => {
        if (e.target.matches('[data-sort-mirror]')) {
          const desktop = this.form.querySelector('#SortBy');
          if (desktop) desktop.value = e.target.value;
        }
        if (e.target.type === 'number') return; // debounced via input
        this.applyFilters();
      });
      this.form.addEventListener('input', (e) => {
        if (e.target.type === 'number') this.debouncedSubmit();
      });
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.applyFilters();
      });
      /* desktop dropdown behavior: one group open at a time */
      this.addEventListener(
        'toggle',
        (e) => {
          const group = e.target;
          if (!group.matches?.('.facet-group') || !group.open) return;
          if (window.matchMedia('(min-width: 750px)').matches) {
            this.querySelectorAll('.facet-group[open]').forEach((other) => {
              if (other !== group) other.open = false;
            });
          }
        },
        true
      );

      this.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-facet-remove]');
        if (pill) {
          e.preventDefault();
          this.renderFromUrl(new URL(pill.href, window.location.origin));
        }
        const clear = e.target.closest('[data-facets-clear]');
        if (clear) {
          e.preventDefault();
          this.form.querySelectorAll('input[type="checkbox"]').forEach((c) => (c.checked = false));
          this.form.querySelectorAll('input[type="number"]').forEach((n) => (n.value = ''));
          this.applyFilters();
        }
      });
    }

    serialize() {
      const params = new URLSearchParams();
      for (const el of this.form.elements) {
        if (!el.name || el.disabled) continue;
        if (el.hasAttribute('data-sort-mirror')) continue;
        if (el.type === 'checkbox' && !el.checked) continue;
        if (el.value === '') continue;
        params.append(el.name, el.value);
      }
      return params;
    }

    applyFilters() {
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = this.serialize().toString();
      this.renderFromUrl(url);
    }

    async renderFromUrl(url, { push = true } = {}) {
      const container = document.getElementById('ProductGridContainer');
      if (!container) return;
      /* Preserve the user's filtering context across the swap: whether the
         mobile drawer is open, which facet groups are expanded, and where
         focus was — the drawer node itself is about to be destroyed. */
      const drawerWasOpen = !!container.querySelector('facet-drawer.is-open');
      const openGroups = [...container.querySelectorAll('.facet-group[open]')].map((g) => g.dataset.facetIndex);
      const active = document.activeElement;
      const focusName = container.contains(active) ? active.name : null;
      const focusValue = container.contains(active) ? active.value : null;

      /* Guard against out-of-order responses reverting newer selections. */
      activeController?.abort();
      const controller = (activeController = new AbortController());
      container.classList.add('is-loading');
      try {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' }, signal: activeController.signal });
        if (!response.ok) throw new Error(String(response.status)); // fall through to full navigation
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.getElementById('ProductGridContainer');
        if (fresh) {
          if (drawerWasOpen) window.MBTheme.releaseFocus(); // trap references dying nodes
          container.innerHTML = fresh.innerHTML;
          if (push) history.pushState({}, '', url);
          openGroups.forEach((index) => {
            const group = container.querySelector(`.facet-group[data-facet-index="${CSS.escape(index)}"]`);
            if (group) group.open = true;
          });
          const count = container.querySelector('#ProductCountTop');
          if (count) count.setAttribute('aria-live', 'polite');
          /* only reopen the mobile drawer if the user hasn't dismissed it while
             the fetch was in flight (else it pops back open and traps focus) */
          if (drawerWasOpen && !drawerDismissed) {
            const drawer = container.querySelector('facet-drawer');
            drawer?.open();
          }
          /* restore focus on BOTH desktop (inline facets) and mobile so keyboard
             users keep their place after every checkbox/price change */
          let restored = false;
          if (focusName) {
            const restore = container.querySelector(
              focusValue ? `[name="${CSS.escape(focusName)}"][value="${CSS.escape(focusValue)}"]` : `[name="${CSS.escape(focusName)}"]`
            );
            if (restore) {
              restore.focus({ preventScroll: true });
              // put the caret back at the end for typed price fields
              if (restore.type === 'number' || restore.type === 'text') {
                const v = restore.value;
                restore.value = '';
                restore.value = v;
              }
              restored = true;
            }
          }
          // only jump to the top when focus wasn't preserved (avoids yanking
          // the page while a keyboard user is mid-interaction)
          if (!drawerWasOpen && !restored) {
            container.scrollIntoView({ block: 'start', behavior: 'auto' });
          }
          const status = document.querySelector('[data-cart-status]');
          if (status && count) status.textContent = count.textContent.trim();
        }
      } catch (e) {
        if (e.name === 'AbortError') return; // superseded by a newer request
        window.location.assign(url); // graceful fallback: full navigation
      } finally {
        // only the current request may clear the busy state — a superseded
        // request's cleanup would strip it while the newer fetch is in flight
        if (controller === activeController) container.classList.remove('is-loading');
      }
    }
  }
  customElements.define('facet-filters-form', FacetFiltersForm);

  class FacetDrawer extends HTMLElement {
    connectedCallback() {
      this.openButton = document.querySelector('[data-facet-drawer-open]');
      this.openButton?.addEventListener('click', () => this.open());
      this.bindCloses();
      this.addEventListener('keyup', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }
    bindCloses() {
      this.querySelectorAll('[data-facet-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    }
    open() {
      drawerDismissed = false;
      this.classList.add('is-open');
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      // becomes a real modal only now (attributes withheld statically so the
      // inline desktop panel isn't announced as a dialog — see facets.liquid)
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      window.MBTheme.trapFocus(this.querySelector('.facets__panel-inner'), this.querySelector('[data-facet-drawer-close]'));
    }
    close() {
      drawerDismissed = true;
      this.classList.remove('is-open');
      this.openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('role');
      this.removeAttribute('aria-modal');
      window.MBTheme.releaseFocus();
      this.openButton?.focus();
    }
  }
  customElements.define('facet-drawer', FacetDrawer);

  /* One page-level popstate handler (element instances are swapped on render) */
  window.addEventListener('popstate', () => {
    document.querySelector('facet-filters-form')?.renderFromUrl(new URL(window.location.href), { push: false });
  });

  /* Close desktop facet dropdowns when clicking outside */
  document.addEventListener('click', (e) => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    document.querySelectorAll('facet-filters-form .facet-group[open]').forEach((group) => {
      if (!group.contains(e.target)) group.open = false;
    });
  });
})();
